import { useState, type FormEvent } from 'react';
import { SpinnerGap, ShieldCheck } from '@phosphor-icons/react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useBranding } from '@/lib/branding/BrandingProvider';

export default function LoginPage() {
  const brand = useBranding();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: err } = await signIn(email.trim(), password);
    if (err) setError(err);
    setSubmitting(false);
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-neurax-ink">
      <div className="absolute inset-0 bg-neurax-glow" />
      <div className="absolute inset-0 grid-bg opacity-30" />

      <div className="relative z-10 mx-auto grid min-h-screen w-full max-w-6xl grid-cols-1 lg:grid-cols-2">
        <div className="hidden flex-col justify-between p-12 lg:flex">
          <div className="flex flex-col gap-2">
            <img
              src={brand.logoUrl}
              alt={brand.name}
              className="h-14 w-auto max-w-[260px] object-contain self-start drop-shadow-[0_0_22px_rgba(91,124,255,0.4)]"
            />
            <p className="text-[11px] uppercase tracking-[0.22em] text-neurax-electric/80">
              {brand.productName}
            </p>
          </div>

          <div>
            <h1 className="font-display text-5xl font-semibold leading-[1.05] text-white">
              Real-time interview
              <br />
              <span className="text-gradient-electric">intelligence</span>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground">{brand.tagline}</p>

            <div className="mt-10 grid grid-cols-2 gap-3">
              {[
                { k: 'Live device sync', v: 'Mac · Windows' },
                { k: 'Earpiece prompts', v: '~7K tokens' },
                { k: 'Per-candidate license', v: 'Revocable' },
                { k: 'Tenant isolation', v: 'RLS-enforced' },
              ].map((s) => (
                <div key={s.k} className="glass-card p-4">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {s.k}
                  </p>
                  <p className="mt-1 font-display text-sm font-semibold text-white">{s.v}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </p>
        </div>

        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8 flex items-center justify-center">
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="h-12 w-auto max-w-[220px] object-contain"
              />
            </div>

            <div className="glass-panel p-8 shadow-glow-soft">
              <div className="mb-6 flex items-center gap-2 text-xs text-neurax-electric">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span className="uppercase tracking-[0.2em]">Secure tenant access</span>
              </div>
              <h2 className="font-display text-2xl font-semibold text-white">
                Sign in to your portal
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Use the credentials provisioned for your {brand.name} account.
              </p>

              <form onSubmit={onSubmit} className="mt-7 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Work email
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Password
                  </label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:brightness-110 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <SpinnerGap className="h-4 w-4 animate-spin" />
                      Signing you in…
                    </>
                  ) : (
                    'Continue to portal'
                  )}
                </button>

                <p className="pt-2 text-center text-xs text-muted-foreground">
                  Trouble logging in? Email{' '}
                  <a
                    className="text-neurax-electric hover:underline"
                    href={`mailto:${brand.supportEmail}`}
                  >
                    {brand.supportEmail}
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
